import {css} from '@emotion/react';
import styled from '@emotion/styled';
import {ChangeEventHandler, FocusEventHandler, InputHTMLAttributes, useState} from 'react';

interface ContainerProps {
    validity: boolean;
    validated: boolean;
    error: string | undefined;
}

const Container = styled.div<ContainerProps>`
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 15px 0;
    box-sizing: border-box;


    ${({validity, validated, error}) =>
            !validity && validated && css`
                margin-bottom: 28px;

                &::before {
                    content: '';
                    position: absolute;
                    bottom: -20px;
                    left: 2px;
                    width: 16px;
                    height: 16px;
                    padding-left: 20px;
                }

                &::after {
                    content: "${error}";
                    position: absolute;
                    bottom: -20px;
                    left: 22px;
                    width: calc(100% - 20px);
                    text-align: left;
                    color: red;
                    font-size: 12px;
                }
            `
    }
`;

interface InputProps {
    validated: boolean;
}

const StyledInput = styled.input<InputProps>`
    border-radius: 14px;
    background-color: #FAFAFA;
    border: 1px solid #ECEBEB;
    width: 100%;
    height: 62px;
    padding-left: 21px;
    box-sizing: border-box;

    color: #333;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    &::placeholder {
        color: #545454;
        font-size: 15px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    &:focus {
        border: 2px solid #2F80ED;
        outline: none;
    }

    ${({validated}) =>
            validated && css`
                &:invalid {
                    &:focus {
                        border: 2px solid red;
                    }
                }
            `
    }
`;

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    setValue: (value: string) => void;
    error: string;
}

export default function Input({setValue, error, ...props}: Props) {
    const [validity, setValidity] = useState(false);
    const [validated, setValidated] = useState(false);

    const blur: FocusEventHandler<HTMLInputElement> = (event) => {
        setValidated(true);
        setValidity(event.target.validity.valid);
    }

    const change: ChangeEventHandler<HTMLInputElement> = (event) => {
        setValidity(event.target.validity.valid);
        if (setValue) setValue(event.target.value);
    }

    return (
        <Container error={error} validity={validity} validated={validated}>
            <StyledInput validated={validated} onChange={change} onBlurCapture={blur} {...props} />
        </Container>
    );
}
